import { cn } from '@dfh-corp-sites/ui/utils'
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image'
import React, { useMemo } from 'react'

import { GlobalSettingsQuery } from '../../../graphql/gatsby'
import { useGlobalSettings } from '../../../hooks/useGlobalSettings'
import { getGatsbyImageDataForAsset } from '../../../utils/sanity/image'
import Link from '../../global/Link'

export interface TopSearchProps {
  searcher: NonNullable<
    NonNullable<GlobalSettingsQuery['sanitySearchModalContent']>['topSearches']
  >[number]
  size?: 'md' | 'lg'
}

export const TopSearch = ({ searcher, size }: TopSearchProps) => {
  const images = useMemo(() => {
    const tabletWidth = size === 'lg' ? 144 : 128
    const desktopWidth = size === 'lg' ? 256 : 208

    const tabletImageData = getGatsbyImageDataForAsset(searcher?.image, {
      width: tabletWidth,
      placeholder: 'blurred',
    })
    const desktopImageData = getGatsbyImageDataForAsset(searcher?.image, {
      width: desktopWidth,
      placeholder: 'blurred',
    })

    return tabletImageData && desktopImageData
      ? withArtDirection(desktopImageData, [
          {
            image: tabletImageData,
            media: '(max-width: 1024px)',
          },
          {
            image: desktopImageData,
            media: '(min-width: 1280px)',
          },
        ])
      : desktopImageData
  }, [size, searcher?.image])

  return (
    <li className="flex flex-col justify-between items-center gap-2.5 md:gap-5 text-center relative">
      <p className="text-sm md:text-base xl:text-[18px] !leading-full text-black font-medium ">
        <Link
          className='after:absolute after:inset-0 after:content-[""] after:hover:cursor-pointer after:z-10'
          to={
            searcher?.category ? `/products?category=${encodeURIComponent(searcher.category)}` : '#'
          }
        >
          {searcher?.title}
        </Link>
      </p>
      {images && (
        <div
          className={cn(
            'size-32 xl:size-52 rounded-lg overflow-hidden',
            size === 'lg' && 'md:size-36 xl:size-64',
          )}
        >
          <GatsbyImage className="object-cover" alt="" image={images} />
        </div>
      )}
    </li>
  )
}

export interface TopSearchesListProps {
  size?: 'md' | 'lg'
  className?: string
}

const TopSearchesList: React.FC<TopSearchesListProps> = ({ size = 'md', className }) => {
  const { sanitySearchModalContent } = useGlobalSettings()

  return (
    <ul
      className={cn('flex gap-2.5 md:gap-5 justify-between items-stretch overflow-auto', className)}
    >
      {sanitySearchModalContent?.topSearches?.reduce((acc, searcher) => {
        if (searcher) {
          acc.push(<TopSearch key={searcher?.category} searcher={searcher} size={size} />)
        }
        return acc
      }, [] as React.ReactNode[])}
    </ul>
  )
}

export default TopSearchesList
